import { memo, useMemo, type CSSProperties } from 'react'

import { cn } from '@/lib/utils'

import { dayStatusOf, hasWorker, type ScheduleWorker } from '../utils/eligibility'
import { isUnassignedRow, type ScheduleJobRow } from '../utils/job-rows'
import type { ScheduleBoard } from '../utils/schedule-board'
import type { ScheduleSelection } from '../utils/selection'
import { CompletedIcon, WarningIcon, WorkerAvatar } from './ScheduleBits'
import { BRAND, categoryTone, COMPLETED_TONE, describeDay, LEAVE_HATCH, OFF_HATCH, OUTSIDE_SHIFT_HATCH, plural, PROBLEM_RED } from './schedule-styles'
import { jobAriaLabel, jobTip, staffableJob, VERDICT_CLASS, workerVerdict } from './WeekBoard'

/** Visible hours: 06:00 to 19:00. */
const RANGE_START = 6 * 60
const RANGE_END = 19 * 60
const SPAN = RANGE_END - RANGE_START
const HOURS = Array.from({ length: (RANGE_END - RANGE_START) / 60 }, (_, index) => `${String(6 + index).padStart(2, '0')}:00`)
const ROW_HEIGHT = 42
const MIN_WIDTH_PCT = 3
const GRID_TEMPLATE = '160px minmax(0, 1fr)'
const HOUR_LINES: CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, transparent calc(100% - 1px), rgba(20,35,27,0.08) calc(100% - 1px))',
  backgroundSize: `${100 / HOURS.length}% 100%`,
}

const pct = (minute: number) => ((Math.max(RANGE_START, Math.min(RANGE_END, minute)) - RANGE_START) / SPAN) * 100

type TimedJob = ScheduleJobRow & { startMinute: number; endMinute: number }

const isTimed = (job: ScheduleJobRow): job is TimedJob => job.startMinute != null && job.endMinute != null

/** Overlapping jobs stack into sub-rows (first sub-row that is free at the job's start). */
function stack(jobs: TimedJob[]) {
  const ends: number[] = []
  const items = [...jobs]
    .sort((left, right) => left.startMinute - right.startMinute)
    .map((job) => {
      let row = ends.findIndex((end) => end <= job.startMinute)
      if (row === -1) row = ends.length
      ends[row] = job.endMinute
      return { job, row }
    })
  return { items, rows: Math.max(1, ends.length) }
}

const position = (job: TimedJob, row: number): CSSProperties => {
  const left = pct(job.startMinute)
  const right = pct(job.endMinute)
  const width = Math.max(right - left, MIN_WIDTH_PCT)
  return { left: `${Math.min(left, 100 - width)}%`, width: `${width}%`, top: 6 + row * ROW_HEIGHT, height: 38 }
}

function TimelineBlock({
  job,
  row,
  problemText,
  open,
  selected,
  onPick,
}: {
  job: TimedJob
  row: number
  problemText: string | null
  open: boolean
  selected: boolean
  onPick: (job: ScheduleJobRow) => void
}) {
  const tone = job.isCompleted ? COMPLETED_TONE : open ? { bg: '#FFF4D6', fg: '#5E3C00' } : categoryTone(job.categoryLabel)
  const clippedStart = job.startMinute < RANGE_START
  const clippedEnd = job.endMinute > RANGE_END
  return (
    <button
      type="button"
      onClick={() => onPick(job)}
      title={problemText ? `${jobTip(job)} · Problem: ${problemText}` : jobTip(job)}
      aria-label={jobAriaLabel(job, problemText)}
      aria-pressed={selected}
      className="absolute overflow-hidden rounded-lg px-1.5 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B] focus-visible:ring-offset-1"
      style={{
        ...position(job, row),
        background: tone.bg,
        color: tone.fg,
        border: open ? '2px dashed #A56A00' : `2px solid ${problemText ? PROBLEM_RED : 'transparent'}`,
        boxShadow: selected ? '0 0 0 2px #14231B' : undefined,
        zIndex: 1,
      }}
    >
      <span className="flex items-center gap-1 truncate text-xs font-bold">
        {job.isCompleted ? <CompletedIcon /> : problemText && !open ? <WarningIcon /> : null}
        {clippedStart ? <span aria-hidden="true">‹</span> : null}
        <span className="truncate">{job.title}</span>
        {clippedEnd ? <span aria-hidden="true">›</span> : null}
      </span>
      <span className="block whitespace-nowrap text-xs tabular-nums">{job.timeLabel}</span>
    </button>
  )
}

/** Jobs without a start time can't be placed on the timeline: listed as buttons instead. */
function UntimedJobs({ jobs, onPick }: { jobs: ScheduleJobRow[]; onPick: (job: ScheduleJobRow) => void }) {
  if (jobs.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-1 py-1 text-xs text-[#4A5A51]">
      <span className="font-semibold">No time set:</span>
      {jobs.map((job) => (
        <button
          key={job.key}
          type="button"
          onClick={() => onPick(job)}
          className="min-h-7 rounded-lg border border-[#C9D3C8] bg-white px-2 font-semibold text-[#14231B] hover:bg-[#F4F9F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B]"
        >
          {job.title}
        </button>
      ))}
    </div>
  )
}

const WorkerLane = memo(function WorkerLane({
  worker,
  day,
  jobs,
  board,
  selectedJob,
  isSelectedWorker,
  onPickJob,
  onPickWorker,
}: {
  worker: ScheduleWorker
  day: string
  jobs: ScheduleJobRow[]
  board: ScheduleBoard
  selectedJob: ScheduleJobRow | null
  isSelectedWorker: boolean
  onPickJob: (job: ScheduleJobRow) => void
  onPickWorker: (worker: ScheduleWorker) => void
}) {
  const status = dayStatusOf(worker, day)
  const timed = jobs.filter(isTimed)
  const layout = stack(timed)
  const verdictJob = staffableJob(selectedJob)
  const verdict = verdictJob ? board.check(worker, verdictJob) : null
  const fallback = status.kind === 'shift' ? `Shift ${status.label}` : status.kind === 'leave' ? 'On leave' : status.kind === 'off' ? 'Day off' : 'Hours not set'
  const label = workerVerdict(worker, verdictJob, verdict, fallback)

  let track: CSSProperties = OUTSIDE_SHIFT_HATCH
  let laneLabel: { text: string; className: string } | null = null
  if (status.kind === 'leave') {
    track = LEAVE_HATCH
    laneLabel = { text: status.leaveType ? `On leave · ${status.leaveType}` : 'On leave', className: 'text-[#42237A]' }
  } else if (status.kind === 'off') {
    track = OFF_HATCH
    laneLabel = { text: 'Day off', className: 'text-[#2F3E36]' }
  } else if (status.kind === 'unset') {
    track = { background: '#FFFFFF' }
    laneLabel = { text: 'Hours not set', className: 'font-semibold text-[#4A5A51]' }
  }
  const dimTrack = Boolean(verdictJob && verdictJob.date === day && verdict && !verdict.ok)
  const ghost = verdictJob && verdictJob.date === day && verdict?.ok && !hasWorker(verdictJob, worker.id) && isTimed(verdictJob) ? verdictJob : null

  return (
    <div className="grid border-b border-[#E3EAE2]" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
      <button
        type="button"
        onClick={() => onPickWorker(worker)}
        aria-label={`Show open jobs for ${worker.name}`}
        aria-pressed={isSelectedWorker}
        className={cn(
          'flex min-h-11 items-center gap-2.5 px-3.5 py-2 text-left text-[#14231B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#14231B]',
          isSelectedWorker ? 'bg-[#E7F5EC]' : 'bg-white hover:bg-[#F4F9F5]',
        )}
      >
        <WorkerAvatar name={worker.name} dim={label.dim} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{worker.name}</span>
          <span className={cn('block text-xs', VERDICT_CLASS[label.tone])}>{label.text}</span>
        </span>
      </button>
      <div className="min-w-0 border-l border-[#E3EAE2]">
        <div className="relative" style={{ ...track, height: layout.rows * ROW_HEIGHT + 6, opacity: dimTrack ? 0.55 : 1 }}>
          {status.kind === 'shift'
            ? status.ranges.map(([from, to]) => (
              <div key={`${from}-${to}`} className="absolute inset-y-0 bg-[#E9F5EC]" style={{ left: `${pct(from)}%`, width: `${pct(to) - pct(from)}%` }} />
            ))
            : null}
          <div className="pointer-events-none absolute inset-0" style={HOUR_LINES} />
          {laneLabel ? (
            <div className={cn('absolute inset-y-0 right-2.5 flex items-center text-[13px] font-bold', laneLabel.className)}>{laneLabel.text}</div>
          ) : null}
          {ghost ? (
            <div
              className="absolute flex items-center justify-center rounded-lg bg-white/70 text-xs font-bold text-[#14532D]"
              style={{ ...position(ghost, 0), border: `2px dashed ${BRAND}` }}
              aria-hidden="true"
            >
              Fits
            </div>
          ) : null}
          {layout.items.map(({ job, row }) => {
            const result = board.check(worker, job)
            return (
              <TimelineBlock
                key={job.key}
                job={job}
                row={row}
                problemText={result.ok ? null : result.text}
                open={false}
                selected={selectedJob?.key === job.key}
                onPick={onPickJob}
              />
            )
          })}
        </div>
        <UntimedJobs jobs={jobs.filter((job) => !isTimed(job))} onPick={onPickJob} />
      </div>
    </div>
  )
})

/** One day: day pills, hour axis, the "Needs a worker" lane and one lane per worker. */
export function DayBoard({
  board,
  day,
  selection,
  onPickDay,
  onPickJob,
  onPickWorker,
}: {
  board: ScheduleBoard
  day: string
  selection: ScheduleSelection
  onPickDay: (day: string) => void
  onPickJob: (job: ScheduleJobRow) => void
  onPickWorker: (worker: ScheduleWorker) => void
}) {
  const selectedJob = selection?.type === 'job' ? board.jobByKey.get(selection.key) ?? null : null
  const dayJobs = useMemo(() => board.jobs.filter((job) => job.date === day), [board.jobs, day])
  const openJobs = useMemo(() => dayJobs.filter(isUnassignedRow), [dayJobs])
  const openLayout = useMemo(() => stack(openJobs.filter(isTimed)), [openJobs])
  const jobsByWorker = useMemo(() => {
    const map = new Map<string, ScheduleJobRow[]>()
    dayJobs.forEach((job) => job.workers.forEach(({ workerId }) => map.set(workerId, [...(map.get(workerId) ?? []), job])))
    return map
  }, [dayJobs])
  const toFixByDay = useMemo(() => {
    const map = new Map<string, number>()
    board.attention.forEach((item) => map.set(item.job.date, (map.get(item.job.date) ?? 0) + 1))
    return map
  }, [board.attention])

  return (
    <>
      <div className="flex flex-wrap gap-1.5 px-5 pb-3" role="group" aria-label="Pick a day">
        {board.days.map((pillDay) => {
          const info = describeDay(pillDay)
          const toFix = toFixByDay.get(pillDay) ?? 0
          const active = pillDay === day
          return (
            <button
              key={pillDay}
              type="button"
              onClick={() => onPickDay(pillDay)}
              aria-pressed={active}
              aria-label={`${info.long}${toFix ? `, ${plural(toFix, 'job')} need attention` : ''}`}
              className={cn(
                'inline-flex min-h-11 items-center gap-2 rounded-[10px] border px-3.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B]',
                active ? 'bg-[#E7F5EC] text-[#14231B]' : 'border-[#DDE4DC] bg-white text-[#3C4B43] hover:bg-[#F4F9F5]',
              )}
              style={active ? { borderColor: BRAND } : undefined}
            >
              <span className="font-semibold">{info.dow}</span>
              <span className="font-bold">{info.num}</span>
              {toFix ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B42318] px-[5px] text-xs font-bold text-white">{toFix}</span>
              ) : null}
            </button>
          )
        })}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[780px]">
          <div className="grid border-y border-[#E6ECE5]" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
            <div className="flex min-h-9 items-end px-3.5 pb-2 text-xs font-bold uppercase tracking-[0.6px] text-[#4A5A51]">Worker</div>
            <div className="flex items-end border-l border-[#E3EAE2]">
              {HOURS.map((hour) => (
                <div key={hour} className="min-w-0 flex-1 pb-2 pl-1 text-xs font-semibold tabular-nums text-[#3C4B43]">{hour}</div>
              ))}
            </div>
          </div>

          <div className="grid border-b border-[#E3EAE2] bg-[#FFFBEE]" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
            <div className="flex items-center px-3.5 py-2.5 text-sm font-bold text-[#5E3C00]">Needs a worker</div>
            <div className="min-w-0 border-l border-[#E3EAE2]">
              <div className="relative" style={{ height: openLayout.rows * ROW_HEIGHT + 6 }}>
                <div className="pointer-events-none absolute inset-0" style={HOUR_LINES} />
                {openJobs.length === 0 ? (
                  <div className="absolute inset-y-0 left-3 flex items-center text-[13px] text-[#4A5A51]">Every job on this day has a worker.</div>
                ) : null}
                {openLayout.items.map(({ job, row }) => (
                  <TimelineBlock
                    key={job.key}
                    job={job}
                    row={row}
                    problemText={board.issueByJobKey.get(job.key)?.text ?? 'Needs a worker'}
                    open
                    selected={selectedJob?.key === job.key}
                    onPick={onPickJob}
                  />
                ))}
              </div>
              <UntimedJobs jobs={openJobs.filter((job) => !isTimed(job))} onPick={onPickJob} />
            </div>
          </div>

          {board.workers.length === 0 ? (
            <p className="px-5 py-6 text-sm text-[#4A5A51]">No workers have jobs, availability or leave in this week.</p>
          ) : (
            board.workers.map((worker) => (
              <WorkerLane
                key={worker.id}
                worker={worker}
                day={day}
                jobs={jobsByWorker.get(worker.id) ?? []}
                board={board}
                selectedJob={selectedJob}
                isSelectedWorker={selection?.type === 'worker' && selection.id === worker.id}
                onPickJob={onPickJob}
                onPickWorker={onPickWorker}
              />
            ))
          )}
        </div>
      </div>
    </>
  )
}
